import React, { useCallback, useMemo, useState } from 'react';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Switch } from '@cotera/client/v0/components/ui/switch';
import type { WorkflowEntryRunInputSpec } from './workflow-entry-run-input';
import {
  buildRunInputFromFormValues,
  defaultSampleForEntryRunInputSpec,
  validateEntryRunInputFormValues,
} from './workflow-entry-run-input';

function initialFormValues(
  spec: WorkflowEntryRunInputSpec
): Record<string, unknown> {
  const sample = defaultSampleForEntryRunInputSpec(spec);
  if (spec.kind === 'empty') {
    return {};
  }
  if (spec.kind === 'scalar') {
    return { __scalar__: sample };
  }
  if (typeof sample === 'object' && sample !== null && !Array.isArray(sample)) {
    return { ...(sample as Record<string, unknown>) };
  }
  return {};
}

function coerceFieldValue(
  fieldType: string,
  raw: string
): string | number | boolean {
  if (fieldType === 'number') {
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (fieldType === 'true/false') {
    return raw === 'true';
  }
  return raw;
}

export const WorkflowTriggerWorkflowAskView: React.FC<{
  spec: WorkflowEntryRunInputSpec;
  onRespond: (runInput: unknown) => void | Promise<void>;
  onCancel: () => void | Promise<void>;
}> = ({ spec, onRespond, onCancel }) => {
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    initialFormValues(spec)
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const title = useMemo(() => {
    if (spec.kind === 'empty') {
      return 'Run workflow';
    }
    return 'Trigger workflow';
  }, [spec.kind]);

  const handleSubmit = useCallback(async () => {
    const error = validateEntryRunInputFormValues(spec, values);
    if (error !== null) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    await onRespond(buildRunInputFromFormValues(spec, values));
  }, [onRespond, spec, values]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <Text.Title type="title">{title}</Text.Title>
        {spec.kind === 'empty' ? (
          <Text.P className="text-muted-foreground text-sm">
            This workflow has no required inputs.
          </Text.P>
        ) : (
          <Text.P className="text-muted-foreground text-sm">
            Provide the inputs for the workflow entry point.
          </Text.P>
        )}
      </div>

      {spec.kind === 'struct' ? (
        <div className="flex flex-col gap-3">
          {spec.fields.map((field) => (
            <div key={field.name} className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">
                {field.name}
                {field.required ? (
                  <span className="text-muted-foreground ml-1 text-xs">
                    (required)
                  </span>
                ) : null}
              </label>
              {field.type === 'true/false' ? (
                <Switch
                  checked={values[field.name] === true}
                  onCheckedChange={(checked) => {
                    setValues((current) => ({
                      ...current,
                      [field.name]: checked,
                    }));
                  }}
                />
              ) : (
                <Input
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={
                    values[field.name] !== undefined &&
                    values[field.name] !== null
                      ? String(values[field.name])
                      : ''
                  }
                  onChange={(event) => {
                    const raw = event.target.value;
                    setValues((current) => ({
                      ...current,
                      [field.name]: coerceFieldValue(field.type, raw),
                    }));
                  }}
                  placeholder={field.type}
                />
              )}
            </div>
          ))}
        </div>
      ) : null}

      {spec.kind === 'scalar' ? (
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground">Input</label>
          {spec.type === 'true/false' ? (
            <Switch
              checked={values['__scalar__'] === true}
              onCheckedChange={(checked) => {
                setValues({ __scalar__: checked });
              }}
            />
          ) : (
            <Input
              type={spec.type === 'number' ? 'number' : 'text'}
              value={
                values['__scalar__'] !== undefined &&
                values['__scalar__'] !== null
                  ? String(values['__scalar__'])
                  : ''
              }
              onChange={(event) => {
                const raw = event.target.value;
                setValues({
                  __scalar__: coerceFieldValue(spec.type, raw),
                });
              }}
              placeholder={spec.type}
            />
          )}
        </div>
      ) : null}

      {validationError !== null ? (
        <Text.P className="text-sm text-red-600">{validationError}</Text.P>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => void onCancel()}>
          Cancel
        </Button>
        <Button type="button" onClick={() => void handleSubmit()}>
          Run
        </Button>
      </div>
    </div>
  );
};
